import downloadFile from 'downloadjs';

import { closeNotification, danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post } from 'utils/api';
import { getAccessParams } from 'utils/shared-access';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import { snakeCaseKeys } from 'utils/keys';
import { Querystring } from 'utils/url';

import type { Action, Dispatch, GetState, ThunkAction } from 'types';
import type {
  SharedAccessOutfit,
  SharedAccessOutfitCreator,
  SharedAccessOutfitsState
} from '../types/shared-access-outfits.type';
import type { AxiosRequestConfig } from 'axios';

applyTranslations(require('./sharedAccessOutfits.i18n.json'));

const RESET = 'sharedAccessOutfits/RESET';
const LOADED = 'sharedAccessOutfits/LOADED';
const SET_LOADING_STATE = 'sharedAccessOutfits/SET_LOADING_STATE';
const UPDATE_OUTFIT = 'sharedAccessOutfits/UPDATE_OUTFIT';
const ADD_ITEM = 'sharedAccessOutfits/ADD_ITEM';
const ADD_OUTFIT = 'sharedAccessOutfits/ADD_OUTFIT';

const OUTFIT_ITEMS = 6;

type State = {
  influencer: SharedAccessOutfitCreator,
  currentState: SharedAccessOutfitsState,
  outfits: Array<SharedAccessOutfit>
};

const initialState: State = {
  currentState: 'initial',
  outfits: [],
  influencer: {
    artistName: '',
    firstName: '',
    lastName: ''
  }
};

export function resetSharedAccessOutfits(): Action {
  return {
    type: RESET
  };
}

function dataLoaded(
  influencer: SharedAccessOutfitCreator,
  outfits: Array<SharedAccessOutfit>,
  currentState?: SharedAccessOutfitsState
): Action {
  return {
    type: LOADED,
    payload: {
      influencer,
      outfits,
      currentState
    }
  };
}

function setLoadingState(loadingState: SharedAccessOutfitsState): Action {
  return {
    type: SET_LOADING_STATE,
    payload: {
      loadingState
    }
  };
}

export function updateOutfitInfo(outfitIndex: number, outfit: SharedAccessOutfit): Action {
  return {
    type: UPDATE_OUTFIT,
    payload: {
      outfitIndex,
      outfit
    }
  };
}

function getMappedOutfits(outfits: Array<SharedAccessOutfit>): Array<SharedAccessOutfit> {
  const baseOutfit: SharedAccessOutfit = {
    postType: 'other',
    items: []
  };
  const mappedOutfits = !outfits.length ? [baseOutfit] : outfits;
  return mappedOutfits
    .map((_, index) => {
      const items = mappedOutfits[index].items ?? [];
      const itemsCount = Math.max(mappedOutfits[index].items.length, OUTFIT_ITEMS);
      const mappedItems = Array.from(Array(itemsCount))
        .map((__, itemIndex) => items[itemIndex] ?? {});

      return {
        ...mappedOutfits[index],
        items: mappedItems
      };
    });
}


export function fetchSharedAccessOutfits(): ThunkAction {
  return async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    const { config, options } = getAccessParams(getState);
    const url = API.END_POINTS.SHARED_OUTFITS.sharedOutfit();

    try {
      dispatch(showLoading(url));
      const { data } = await get(url, config, options);
      const { outfits, creator } = data;

      // since we will always have 6 items per outfit.
      const mappedOutfits = getMappedOutfits(outfits);

      dispatch(dataLoaded(creator, mappedOutfits));
    } catch (e) {
      logError(e);
      dispatch(danger(__t('shared_access_outfits_reducer.outfits_fetch_failed')));
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function addOutfitItem(outfitIndex: number): Action {
  return {
    type: ADD_ITEM,
    payload: {
      outfitIndex
    }
  };
}

export function addOutfit(): Action {
  return {
    type: ADD_OUTFIT
  };
}

export function updateArticleInfo(outfitIndex: number, articleIndex: number, articleUrl: string): ThunkAction {
  return async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    const { config, options } = getAccessParams(getState);

    try {
      const query = Querystring.objectToString(
        snakeCaseKeys({
          urlOrSku: articleUrl
        })
      );
      const url = API.END_POINTS.SHARED_OUTFITS.articleInfo(query);

      const { data } = await get(url, config, options);
      const itemData = data;
      delete itemData.id; // in order to persist original order we need to avoid id overrides

      const { sharedAccessOutfits } = getState();
      const currentOutfit = sharedAccessOutfits.outfits[outfitIndex] ?? {};
      const outfitItems = currentOutfit.items ?? [];

      const updatedOutfit = {
        ...currentOutfit,
        items: currentOutfit.items.map((__, index) => {
          if (index === articleIndex) {
            return itemData;
          }

          return outfitItems[index] ?? {};
        })
      };

      dispatch(updateOutfitInfo(outfitIndex, updatedOutfit));
    } catch (error) {
      logError(error);

      if (error.response?.status === 400) {
        dispatch(danger(__t('shared_access_outfits_reducer.article_info_invalid')));
      } else if (error.response?.status === 404) {
        dispatch(danger(__t('shared_access_outfits_reducer.article_not_in_the_list')));
      } else {
        dispatch(danger(__t('shared_access_outfits_reducer.article_info_failed')));
      }
    }
  };
}

function remapOutfits(outfits: Array<SharedAccessOutfit>): Array<SharedAccessOutfit> {
  return outfits
    .reduce<Array<SharedAccessOutfit>>((result, entry) =>
      result.concat([{
        ...entry,
        items: entry.items.filter(item => item.sku)
      }]), []);
}

export function persistChanges(): ThunkAction {
  return async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    const { config, options } = getAccessParams(getState);
    const url = API.END_POINTS.SHARED_OUTFITS.sharedOutfit();
    const { sharedAccessOutfits } = getState();

    try {
      dispatch(showLoading(url));
      dispatch(setLoadingState('saving'));
      const { data } = await post(url, {
        ...config,
        data: {
          outfits: remapOutfits(sharedAccessOutfits.outfits)
        }
      }, options);

      const { outfits, creator } = data;
      const mappedOutfits = getMappedOutfits(outfits);

      dispatch(dataLoaded(creator, mappedOutfits));
      dispatch(closeNotification());
      dispatch(setLoadingState('saved'));
    } catch (e) {
      logError(e);
      dispatch(setLoadingState('dirty'));
      if (e.response?.data?.code === 'wrong.size') {
        dispatch(danger(__t('shared_access_outfits_reducer.save_error_wrong_size')));
      } else {
        dispatch(danger(__t('shared_access_outfits_reducer.save_outfits_failed')));
      }
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function deleteOutfit(outfitId?: number | null, deletedOutfitIndex?: number | null): ThunkAction {
  return async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    const { sharedAccessOutfits: { influencer, outfits, currentState } } = getState();
    let updatedOutfits;

    if (!outfitId) {
      updatedOutfits = outfits.filter((_, index) => deletedOutfitIndex !== index);
    } else {
      const { config, options } = getAccessParams(getState);
      const url = API.END_POINTS.SHARED_OUTFITS.deleteOutfit(outfitId);

      try {
        dispatch(showLoading(url));
        await destroy(url, config, options);
        updatedOutfits = outfits.filter((outfit) => outfit.id !== outfitId);
        dispatch(success(__t('shared_access_outfits_reducer.outfit_delete_success')));
      } catch (error) {
        logError(error);
        dispatch(danger(__t('shared_access_outfits_reducer.outfits_delete_failed')));
      } finally {
        dispatch(hideLoading(url));
      }
    }

    if (updatedOutfits && updatedOutfits.length) {
      dispatch(dataLoaded(influencer, updatedOutfits, currentState));
    } else if (updatedOutfits) {
      dispatch(fetchSharedAccessOutfits());
    }
  };
}

export function uploadOutfitImage(outfitIndex: number, imageFile: File): ThunkAction {
  return async (dispatch: Dispatch, getState: GetState) => {
    const { config, options } = getAccessParams(getState);
    const url = API.END_POINTS.SHARED_ACCESS.uploadFile();

    try {
      dispatch(showLoading(url));
      const data = new FormData();

      data.append('file', imageFile);

      const response = await post(url, {
        ...config,
        data
      }, options);

      const { sharedAccessOutfits } = getState();
      const currentOutfit = sharedAccessOutfits.outfits[outfitIndex] ?? {};
      const updatedOutfit = {
        ...currentOutfit,
        imageUrl: response.data.url
      };

      dispatch(updateOutfitInfo(outfitIndex, updatedOutfit));
    } catch (error) {
      logError(error);
      dispatch(danger(__t('shared_access_outfits_reducer.image_upload_failed')));
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function downloadOutfits(): ThunkAction {
  const requestConfig: AxiosRequestConfig = {
    responseType: 'blob'
  };
  const url = API.END_POINTS.SHARED_OUTFITS.downloadOutfit();

  return async (dispatch: Dispatch, getState: GetState) => {
    const { config, options } = getAccessParams(getState);
    const { sharedAccessOutfits } = getState();
    const creatorName = sharedAccessOutfits.influencer.artistName;
    const fileName = `Collabary Outfit (${creatorName}).xlsx`;

    try {
      dispatch(showLoading(url));
      const { data } = await get(url, { ...requestConfig, ...config }, options);

      downloadFile(data, fileName);
    } catch (error) {
      logError(error);
      dispatch(danger(__t('shared_access_outfits_reducer.outfits_download_failed')));
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export default createReducer<State, any>('sharedAccessOutfits', initialState, {
  [RESET]: () => initialState,
  [SET_LOADING_STATE]: (state, { loadingState }) => ({
    ...state,
    currentState: loadingState
  }),
  [UPDATE_OUTFIT]: (state, { outfitIndex, outfit }) => ({
    ...state,
    outfits: state.outfits.map((storedOutfit, index) => {
      if (index === outfitIndex) {
        return outfit;
      }

      return storedOutfit;
    }),
    currentState: 'dirty'
  }),
  [LOADED]: (state, { influencer, outfits, currentState }) => ({
    ...state,
    currentState: currentState || 'loaded',
    influencer,
    outfits
  }),
  [ADD_ITEM]: (state, { outfitIndex }) => ({
    ...state,
    outfits: state.outfits.map((storedOutfit, index) => {
      if (index === outfitIndex) {
        return { ...storedOutfit, items: [...storedOutfit.items, {}] };
      }

      return storedOutfit;
    })
  }),
  [ADD_OUTFIT]: (state) => {
    const baseOutfit = {
      postType: 'other',
      items: new Array(OUTFIT_ITEMS).fill({})
    };
    return {
      ...state,
      outfits: [...state.outfits, baseOutfit]
    };
  }
});
